import {
    deleteAllLineAttachmentsAction,
    deleteAttachmentAction,
    updateAvailableAttachmentsToCopyAction,
} from "redux/actions/attachmentActions";
import { eliminateAttachmentByFileId } from "services/attachment/attachmentAPIs";

const { ATTACHMENT_LEVEL } = require("constants/generalConstants");
const { useSelector } = require("react-redux");

const useAttachments = () => {
    /**Active attachments, excluding attachments staged to deleted */
    const attachments =
        useSelector((state) =>
            state.attachment.scanResults?.filter(
                (item) => item.isStagedToDelete !== true
            )
        ) ?? [];

    const stagedToDeleteAttachments =
        useSelector((state) =>
            state.attachment.scanResults?.filter(
                (item) => item.isStagedToDelete
            )
        ) ?? [];

    const sortAttachmentsByDateInDesc = (attachmentList) => {
        attachmentList.sort((a, b) => {
            return new Date(b.CREATED_DATE) - new Date(a.CREATED_DATE);
        });
        return attachmentList;
    };

    /** All line attachments belongs to requisition */
    const lineAttachments = attachments.filter(
        (attachment) => attachment.LEVEL_TYPE === ATTACHMENT_LEVEL.LINE
    );

    /** Method to get all attachments belongs to given requisition line */
    const getAttachmentsByLineId = (lineId) => {
        const lineAttachments = attachments.filter(
            (attachment) =>
                attachment.LEVEL_TYPE === ATTACHMENT_LEVEL.LINE &&
                attachment.ATTACHMENT_LINE === lineId
        );

        return sortAttachmentsByDateInDesc(lineAttachments);
    };

    /** Requisition header attachments */
    const headerAttachments = sortAttachmentsByDateInDesc(
        attachments.filter(
            (attachment) => attachment.LEVEL_TYPE === ATTACHMENT_LEVEL.HEADER
        )
    );

    const attachmentsWithNoRecipients = attachments.filter(
        (attachment) => !attachment.CATEGORY || attachment.CATEGORY.length === 0
    );

    /**
     * This method gets called on user clicks the delete button in attachment review/upload files screen,
     * If the attachment to be deleted is new upload, it will be deleted immediately by calling the delete api
     * if the attachment is old during the amendment, it will be staged and deleted upon successful amendment submission
     * @param {Object} attachment- attachment object to be deleted
     * @param {Object} dispatch - dispatch object from the calling component
     */
    const deleteAttachment = async (attachment, dispatch) => {
        if (!attachment) return;

        const { ATTACHMENT_ID, isNewUpload, isCopiedFile } = attachment;
        if (isNewUpload) {
            const response = await eliminateAttachmentByFileId(
                ATTACHMENT_ID,
                dispatch
            );
            if (isCopiedFile) {
                dispatch(
                    updateAvailableAttachmentsToCopyAction(
                        [attachment.OLD_ATTACHMENT_ID],
                        {
                            isSelectedToCopy: false,
                            isCopied: false,
                        }
                    )
                );
            }
            if (response?.status !== 200) return;
        }

        dispatch(deleteAttachmentAction({ ATTACHMENT_ID }));
    };

    /** Deletes given all line item attachments */
    const deleteLineAttachments = async (lineIndex, dispatch) => {
        const lineAttachmentsToDelete = lineAttachments.filter(
            (x) => x.ATTACHMENT_LINE === lineIndex
        );
        if (lineAttachmentsToDelete?.length > 0) {
            lineAttachmentsToDelete.forEach((attachment) => {
                const { ATTACHMENT_ID, isNewUpload } = attachment;
                if (isNewUpload)
                    eliminateAttachmentByFileId(ATTACHMENT_ID, dispatch, true);
            });
            dispatch(deleteAllLineAttachmentsAction);
        }
    };

    /**  Deletes staged attachments upon successful amendment submission */
    const deleteStagedAttachments = (dispatch) => {
        stagedToDeleteAttachments.forEach((attachment) => {
            eliminateAttachmentByFileId(
                attachment.ATTACHMENT_ID,
                dispatch,
                true
            );
        });
    };

    /**Deletes newly uploaded attachments on requisition form close*/
    const deleteAttachmentsOnFormClose = (dispatch) => {
        const newlyUploadedAttachmentIds = attachments
            .filter((a) => a.isNewUpload)
            .map((a) => a.ATTACHMENT_ID);
        newlyUploadedAttachmentIds.forEach((attachmentId) => {
            eliminateAttachmentByFileId(attachmentId, dispatch, true);
        });
    };

    return {
        attachments,
        lineAttachments,
        headerAttachments,
        attachmentsWithNoRecipients,
        getAttachmentsByLineId,
        deleteAttachment,
        deleteStagedAttachments,
        deleteAttachmentsOnFormClose,
        deleteLineAttachments,
    };
};

export default useAttachments;
